import React from 'react';

import styles from './TranslationDisplay.less';

interface TranslationDisplayProps {
  /** The wording, as recipients will read it. */
  text?: string;
  /**
   * Shown instead when there is no wording for the language on screen — the binding
   * itself, muted, so the block still says where the words are supposed to come from.
   */
  fallback?: React.ReactNode;
  /** Where the words come from, one `group.key` per line. */
  keys: string[];
  /** Edits the wording; omitted, the block is read-only. */
  action?: React.ReactNode;
  /**
   * The whole block opens the editor — it is the label as well as the target. Set
   * without a handler when an ancestor is the trigger (the bound field's popover
   * wraps icon and block as one), so only the affordance is this component's.
   */
  clickable?: boolean;
  onClick?: () => void;
  /**
   * `attached` is the right-hand part of a field, sharing the prefix icon's border.
   * `standalone` sits under a field that still holds the raw value itself.
   */
  variant?: 'attached' | 'standalone';
}

/**
 * A translated value, wherever one is shown: **the wording rendered as itself, with the
 * key or keys it came from beneath it.**
 *
 * One design, because there were two. A field whose whole value is one key rendered the
 * wording inline with its key under it; a field whose value is only partly a translation
 * described the same thing in a sentence below — *"Recipients will see: …"* — with no key
 * at all. Same information, two shapes, and the sentence form buried the wording inside
 * a label instead of showing it.
 *
 * The wording leads and the keys recede, in that order: what recipients read is the
 * point, and the key is how you find or reuse it.
 */
const TranslationDisplay: React.FC<TranslationDisplayProps> = ({
  text,
  fallback,
  keys,
  action,
  clickable,
  onClick,
  variant = 'attached',
}) => (
  <div
    className={styles.display}
    data-variant={variant}
    data-clickable={clickable || onClick ? '1' : undefined}
    onClick={onClick}
  >
    <span className={styles.textRow}>
      {text ? (
        <span className={styles.text}>{text}</span>
      ) : (
        <span className={styles.fallback}>{fallback}</span>
      )}
      {action}
    </span>
    {keys.map((key) => (
      <code key={key} className={styles.key}>
        {key}
      </code>
    ))}
  </div>
);

export default React.memo(TranslationDisplay);
